'use client';

import { createAuthClient } from 'better-auth/react';
import { oauthProviderClient } from '@better-auth/oauth-provider/client';
import {
  emailOTPClient,
  genericOAuthClient,
  inferAdditionalFields,
} from 'better-auth/client/plugins';

/**
 * The browser auth client. Talks to the catch-all route handler at `/api/auth/*`
 * over same-origin requests (cookies "just work", no baseURL needed in the
 * browser). `emailOTPClient` adds the 6-digit verify/reset code methods that pair
 * with the server's emailOTP plugin; `genericOAuthClient` adds `signIn.oauth2`
 * and `oauth2.link` for the instance's OIDC route (src/lib/auth-providers.ts).
 * Both client plugins are always present - they are method shapes only; whether
 * a route actually exists is decided server-side by what is configured.
 *
 * Used by the hand-built /signin and /signup pages and the account page's
 * connected-identities section. The viewer/session that the rest of the app
 * reads is resolved server-side (src/lib/viewer.ts); this client only drives the
 * auth forms and the provider redirects.
 */
export const authClient = createAuthClient({
  plugins: [
    emailOTPClient(),
    genericOAuthClient(),
    // The connector lane (docs/AGENT_ACCESS.md §6): on a page the authorization
    // server sent the person to (/signin, /connect), it adds the page's signed
    // authorization query to every POST, so a sign-in there continues the
    // authorization. On any other page there is no signed query and it adds
    // nothing.
    oauthProviderClient(),
    // The additional user field the server declares (src/lib/auth.ts), so
    // `updateUser({ locale })` is typed here too.
    inferAdditionalFields({ user: { locale: { type: 'string', required: false } } }),
  ],
});

export const { signIn, signUp, signOut, useSession } = authClient;
